import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout.js';

const EFFECTIVE = '12 October 2026';
const PRIVACY_EMAIL = 'privacy@arunavm.com';
const SUPPORT_EMAIL = 'support@arunavm.com';

function LegalLayout({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <PublicLayout>
      <article className="prose">
        <h1>{title}</h1>
        <p className="muted">Effective date: {EFFECTIVE}</p>
        {children}
      </article>
    </PublicLayout>
  );
}

export function PrivacyPage(): JSX.Element {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        Velinko shows the SMS messages and one-time codes from your Android phone in your web
        browser. It is run by an individual developer, who is the data controller. Contact:{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
      </p>

      <h2>What Velinko stores</h2>
      <ul>
        <li>
          <strong>Account:</strong> your email address and a salted hash of your password, or your
          Google account ID if you sign in with Google.
        </li>
        <li>
          <strong>SMS messages</strong> from the phone you pair: sender or recipient phone number,
          message text, time, read status, and any one-time code found in the text. These include
          the phone numbers and messages of people who text you.
        </li>
        <li>
          <strong>Messages you send from the browser:</strong> recipient and text, kept only until
          the phone has sent them.
        </li>
        <li>
          <strong>Device information:</strong> phone model and manufacturer, Android and app
          version, battery level, free storage, network type, sync queue counts, and a Firebase push
          token used to wake the app.
        </li>
        <li>
          <strong>Sign-in records:</strong> IP address, browser user agent and time of each sign-in,
          and the email address used in a failed sign-in.
        </li>
        <li>
          <strong>Access requests:</strong> Velinko is invite-only. If you sign up with an email that
          is not approved yet, that email, your IP address and browser user agent are kept so the
          request can be reviewed.
        </li>
      </ul>
      <p>Velinko has no ads, no analytics or tracking, and never sells or shares your data.</p>

      <h2>Why</h2>
      <p>
        To show your messages and codes in your browser, to send messages you ask it to send, to
        keep syncing reliable, and to protect your account. The legal basis is providing the service
        you asked for (GDPR Art. 6(1)(b)) and, for sign-in records, keeping the service secure (Art.
        6(1)(f)).
      </p>

      <h2>Where it is stored and who processes it</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> (Workers, D1 database, Pages) hosts the service and stores
          your data. Cloudflare may process it outside the EU/EEA under its data processing addendum
          and the EU Standard Contractual Clauses.
        </li>
        <li>
          <strong>Google</strong> handles Google sign-in and Firebase Cloud Messaging. Firebase
          receives only the push token and a wake-up signal, never message content.
        </li>
      </ul>

      <h2>Security</h2>
      <p>
        All traffic uses HTTPS. Data is stored in Cloudflare&apos;s database, which Cloudflare
        encrypts at rest. Messages are <strong>not end-to-end encrypted</strong>: the Velinko server
        can read message text in order to show and search it. Passwords are stored only as salted
        hashes, and device keys only as hashes.
      </p>

      <h2>How long it is kept</h2>
      <ul>
        <li>SMS messages: until you delete them or your account.</li>
        <li>Text of messages sent from the browser: removed once the send has finished.</li>
        <li>Device status records: 30 days. Sign-in records and access requests: 90 days. Device commands: 30 days.</li>
        <li>Expired sessions, tokens and pairing codes: removed daily.</li>
        <li>On the phone, uploaded messages leave the app&apos;s upload queue after 7 days.</li>
        <li>
          After account deletion, data is removed immediately and disappears from database backups
          within 30 days.
        </li>
      </ul>

      <h2>Your rights</h2>
      <p>
        You can download all your data or delete your account at any time on the{' '}
        <Link to="/account">Account</Link> page. You can also ask for access, correction, deletion
        or a copy by emailing <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>, and you can
        object to processing or complain to your local data protection authority.
      </p>

      <h2>Children</h2>
      <p>Velinko is not meant for anyone under 18.</p>

      <h2>Changes</h2>
      <p>Changes to this policy are posted on this page with a new effective date.</p>
    </LegalLayout>
  );
}

export function TermsPage(): JSX.Element {
  return (
    <LegalLayout title="Terms of Service">
      <h2>1. What Velinko does</h2>
      <p>
        Velinko syncs SMS messages from an Android phone you own to your web dashboard, and can send
        SMS from that phone when you ask it to.
      </p>
      <h2>2. Acceptable use</h2>
      <p>
        Use Velinko only with a phone and SIM card you are allowed to use. Do not use it to read or
        send messages without the permission of the phone&apos;s owner, or to send spam.
      </p>
      <h2>3. Your account</h2>
      <p>
        Keep your password private. You can delete your account at any time on the Account page.
        Accounts used in breach of these terms may be suspended or deleted.
      </p>
      <h2>4. Availability</h2>
      <p>
        Velinko is provided as is. Messages can be delayed or missed because of the network, battery
        restrictions or Android. Do not rely on Velinko as your only way to receive important codes.
      </p>
      <h2>5. Liability</h2>
      <p>
        To the extent the law allows, the developer is not liable for indirect losses or for losses
        caused by delayed or missing messages. This does not limit liability for intent or gross
        negligence, or your rights as a consumer under mandatory law.
      </p>
      <h2>6. Contact</h2>
      <p>
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
    </LegalLayout>
  );
}

export function SupportPage(): JSX.Element {
  return (
    <LegalLayout title="Support">
      <p>
        Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for help.
      </p>
      <p>
        Most questions are answered in the <Link to="/docs">docs</Link>, including the{' '}
        <Link to="/docs#samsung">Samsung battery settings</Link> that keep messages syncing and the{' '}
        <Link to="/docs#troubleshooting">troubleshooting</Link> section. To delete your data, see{' '}
        <Link to="/delete-account">Delete your account</Link>.
      </p>
    </LegalLayout>
  );
}

export function DeleteAccountPage(): JSX.Element {
  return (
    <LegalLayout title="Delete your Velinko account">
      <ol>
        <li>Sign in to the Velinko web dashboard.</li>
        <li>
          Open <Link to="/account">Account</Link> and choose{' '}
          <strong>Delete account permanently</strong>.
        </li>
        <li>Uninstall the Velinko app from your phone.</li>
      </ol>
      <p>
        This deletes your account, paired devices, synced messages, commands, sessions and sign-in
        records right away. They disappear from database backups within 30 days. Messages on your
        phone are not touched.
      </p>
      <p>
        If you cannot sign in, email <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> from
        your account&apos;s email address with the subject &quot;Account deletion&quot;.
      </p>
    </LegalLayout>
  );
}
